import { namesAlreadySafe } from './vault/convertLiveClass';
import { STUDENT_PII_FIELDS, STUDENT_DROP_FIELDS } from './studentPiiFields';

/**
 * What a deletion snapshot is allowed to carry.
 *
 * ── THE BUG ─────────────────────────────────────────────────────────────────────────────────────
 *
 * `deletionHistory` exists so a teacher can undo a delete, and to do that it stores the object that
 * was deleted. For a class that object is the in-memory `ClassData`, which carries `studentPrivate` —
 * the hydrated owner-only roster: OENs, parent names, emails and phone numbers, accommodations, notes,
 * report comments, and the real names linked to nickname students. For a student it is the in-memory
 * `Student`, onto which `mergeBackStudentPrivate` has put `realFirstName`/`realLastName`.
 *
 * `addToDeletionHistory` ran `removeUndefined` and nothing else. So deleting a Privacy First class
 * wrote a complete second copy of every child's personal information into another collection — in the
 * one mode whose entire promise is that we do not hold it. A teacher deleting a class to remove the
 * data was creating a copy of it.
 *
 * ── WHY THIS IS SCOPED, NOT GLOBAL ──────────────────────────────────────────────────────────────
 *
 * That data in the snapshot is FUNCTIONAL, not accidental: `createClass` reads
 * `classData.studentPrivate` on restore and splits it back into `classes/{id}/private/roster`
 * (dataService.ts around 423-463 and 588). Strip it for every class and an ordinary teacher who
 * deletes a class by accident gets it back with every parent contact and OEN gone — a worse failure
 * than the one being fixed, and for far more people.
 *
 * So it is stripped exactly where a promise exists: a class in Privacy First or nickname mode. Those
 * classes are not supposed to hold this data at all, so there is nothing legitimate to restore. For an
 * ordinary class the snapshot is undo data for its own owner, bounded by the owner's own retention —
 * a different question, not this one.
 *
 * Once the vault ships, a Privacy First class has no plaintext PII to snapshot in the first place and
 * this becomes belt to the braces rather than the only guard. It stays, because the failure mode is
 * someone reintroducing a merge, not someone deleting this file.
 */

/** Every key that must not appear on a student inside a snapshot of a promised class. */
export const SNAPSHOT_FORBIDDEN_STUDENT_KEYS: readonly string[] = [
  ...STUDENT_PII_FIELDS,
  ...STUDENT_DROP_FIELDS,
];

/** Does this class carry a promise that it holds no personal information? */
export const classPromisesNoPii = (
  cls: { privacyFirst?: boolean; useNicknames?: boolean } | null | undefined,
): boolean => !!(cls?.privacyFirst || cls?.useNicknames);

/**
 * Is THIS STUDENT'S name a placeholder, at the moment they are deleted?
 *
 * `privacyFirstSnapshot` is the flag a restore reads to mean "this record's firstName and lastName
 * are already safe to put on a classmate-readable document". Stamping it from the CLASS was wrong for
 * one window, and it is a window a teacher spends real time in: between pressing "Turn on Privacy
 * First" and finishing the conversion, the class promises no identity while every name on it is
 * still the child's own.
 *
 * Delete "Maya Chen" in that window and the record is stamped. Restore her later and the stamp says
 * her name is a placeholder, so it goes straight back onto the class document, unsealed — and the
 * leak guard cannot see it, because it was never in the vault to be known.
 *
 * So the question is asked per student, with the same signals the conversion planner uses: a
 * self-chosen nickname, a placeholder stamp on the row, a real name already in the vault, or the
 * legacy `realFirstName` in the owner-only roster. Anything else is a real name, and a record holding
 * one must be restored through the planner rather than written back.
 */
export const studentNameIsPlaceholder = (
  cls: { privacyFirst?: boolean; useNicknames?: boolean } | null | undefined,
  student: Record<string, unknown> | null | undefined,
  /** The decrypted vault record for this student, when the caller can read it. */
  vaultRecord: { firstName?: string; lastName?: string } | null | undefined,
  /** `studentPrivateOf(cls, id)` — where the legacy real name lives. */
  rosterPrivate?: Record<string, unknown> | null,
): boolean => {
  if (!classPromisesNoPii(cls)) return false;
  if (!student) return false;
  /**
   * DELEGATED, so there is exactly ONE answer to this question.
   *
   * There were two, and they drifted. This copy returned true on "the vault holds a different name"
   * without requiring the words check the planner required, and it carried a dead
   * `isPseudonymWord` line above an `isIssuedWord` line that subsumed it. Two states had opposite
   * answers at delete time and convert time — harmless today only because the restore makes the
   * planner's answer authoritative whenever a vault record is present.
   *
   * That shape — two signals that must agree about one question, maintained separately — is what
   * produced a HIGH finding in four consecutive review rounds. The comment here used to claim it
   * "mirrors namesAlreadySafe". Now it IS namesAlreadySafe.
   *
   * What stays here is the only part that is genuinely about deletion: a class that promises nothing
   * has no placeholders, whatever its rows look like.
   */
  return namesAlreadySafe(
    student as Parameters<typeof namesAlreadySafe>[0],
    (rosterPrivate ?? undefined) as Record<string, unknown> | undefined,
    vaultRecord,
  );
};

/** Strip the owner-only PII keys off one student object. Returns a new object. */
export function stripStudentPii<T extends Record<string, unknown>>(student: T): T {
  const out: Record<string, unknown> = { ...student };
  for (const f of SNAPSHOT_FORBIDDEN_STUDENT_KEYS) delete out[f];
  return out as T;
}

/**
 * The class object to store for undo.
 *
 * Drops `studentPrivate` wholesale and strips the merged-back keys from every embedded student. Note
 * both are needed: `studentPrivate` is the roster map, and the same values are ALSO merged onto each
 * student by `mergeBackStudentPrivate` for the ~40 display sites — removing one and not the other
 * leaves the data in the snapshot via the other route, which is how this nearly got half-fixed.
 */
export function sanitiseClassSnapshot<T extends Record<string, unknown>>(
  cls: T,
  opts: { force?: boolean } = {},
): T {
  /**
   * `force` exists for the HISTORIC cleanup, and it is not a convenience.
   *
   * Normally the decision comes from the object's own flags, which is right at delete time because the live
   * class is in hand. A snapshot pulled out of `deletionHistory` may not carry them at all — the class may
   * have been sealed after that record was written, or the record may predate the flags — so re-deriving the
   * decision from the data made the cleanup silently do nothing on precisely the records it exists for.
   * Found by a test; the caller has already decided, so it says so.
   */
  // A transient deletion lock must never make a restored class permanently unavailable.
  const { observationDeletionState: _lock, ...unlocked } = cls as Record<string, unknown>;
  if (!opts.force && !classPromisesNoPii(cls as { privacyFirst?: boolean; useNicknames?: boolean })) return unlocked as T;
  const { studentPrivate: _dropped, ...rest } = unlocked;
  const students = Array.isArray(rest.students)
    ? (rest.students as Record<string, unknown>[]).map(stripStudentPii)
    : rest.students;
  return { ...rest, students } as unknown as T;
}

/** The student object to store for undo, given the class it came from. */
export function sanitiseStudentSnapshot<T extends Record<string, unknown>>(
  student: T,
  cls: { privacyFirst?: boolean; useNicknames?: boolean } | null | undefined,
): T {
  return classPromisesNoPii(cls) ? stripStudentPii(student) : student;
}

/**
 * Backstop: does this record still carry something it must not?
 *
 * Returns the offending key paths, or an empty array. Called by `addToDeletionHistory` so a NEW delete
 * path written later cannot bypass the strip by not calling it — the failure mode here is a future call
 * site, not this file.
 */
export function snapshotPiiOffenders(
  record: { type?: string; data?: unknown; privacyFirstSnapshot?: boolean },
): string[] {
  if (!record.privacyFirstSnapshot) return [];
  const offenders: string[] = [];
  const data = record.data as Record<string, unknown> | undefined;
  if (!data || typeof data !== 'object') return [];

  if ('studentPrivate' in data) offenders.push('data.studentPrivate');

  const inspect = (s: unknown, label: string) => {
    if (!s || typeof s !== 'object') return;
    for (const f of SNAPSHOT_FORBIDDEN_STUDENT_KEYS) {
      if (f in (s as Record<string, unknown>)) offenders.push(`${label}.${f}`);
    }
  };

  if (Array.isArray(data.students)) {
    (data.students as unknown[]).forEach((s, i) => inspect(s, `data.students[${i}]`));
  }
  // A student-type record IS the student.
  if (record.type === 'student') inspect(data, 'data');

  /**
   * SECOND ROUTE, and the one that nearly slipped past: a student deletion also stores
   * `studentResidue`, whose declared shape includes `studentPrivate` outright (types.ts). Stripping the
   * student object alone would have left the identical data one field across — which is the same
   * half-fix shape as stripping `studentPrivate` off the class while leaving it merged on each student.
   */
  const residue = (record as { studentResidue?: Record<string, unknown> }).studentResidue;
  if (residue && typeof residue === 'object' && 'studentPrivate' in residue) {
    offenders.push('studentResidue.studentPrivate');
  }

  return offenders;
}

/** The residue to store for undo, given the class the student came from. */
export function sanitiseStudentResidue<T extends Record<string, unknown>>(
  residue: T | undefined,
  cls: { privacyFirst?: boolean; useNicknames?: boolean } | null | undefined,
): T | undefined {
  if (!residue || !classPromisesNoPii(cls)) return residue;
  const { studentPrivate: _dropped, ...rest } = residue as Record<string, unknown>;
  return rest as unknown as T;
}
